import React, {useMemo} from 'react'
import styles from './UIGameTradeCreatorWantedCardSelector.module.scss'
import clsx from 'clsx'
import {CardDataController} from '@colonist/game-card-types'
import {CardEnum} from '@colonist/game-states'
import {UIGameTradeCreatorBankRatio} from '../UIGameTradeCreatorBankRatio'
import {TradeErrorHighlightLocationEnum} from '@colonist/game-mechanic-validators'
import {useGameStore} from '../../../store/GameStoreContext'
import {useCardsToHighlight} from '../useCardsToHighlight'
import {ArrayUtils} from '@colonist/utils'
import {ModeSettingEnum} from '@colonist/types'
import {UIGameCardInventory} from '../../../common/UIGameCardInventory/UIGameCardInventory'
import {cardEnumsToCardState} from '../../../utils/cards'

export interface UIGameTradeCreatorWantedCardSelectorProps {
    className?: string | undefined
    onAddCard: (card: CardEnum) => void
    isPortraitLayout?: boolean | undefined
}

export const UIGameTradeCreatorWantedCardSelector = ({
    className,
    onAddCard,
    isPortraitLayout,
}: UIGameTradeCreatorWantedCardSelectorProps) => {
    const gameModeData = useGameStore((state) => state.gameMode)
    const isBankTradeMode = useGameStore((state) => state.gameTrade.isBankTradeMode)
    const wantedCards = useGameStore((state) => state.gameTrade.wantedCards)
    const beginnerHintData = useGameStore((state) => state.gameBeginnerHint.beginnerHintData)
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const isBeginnerMode = useGameStore((state) => state.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P)

    const wantedResourcesSuggestion: CardEnum[] = useMemo(() => {
        const suggestionWantedCards = beginnerHintData?.data.wantedResources != null ? beginnerHintData?.data.wantedResources : []

        let remainingSuggestions = [...suggestionWantedCards]
        for(const wantedCard of wantedCards) {
            remainingSuggestions = ArrayUtils.removeOneElement(remainingSuggestions, wantedCard)
        }

        return remainingSuggestions
    }, [beginnerHintData, wantedCards])
    const shouldShowSuggestion = beginnerHintData?.data.wantedResources != null

    const handleAddCard = (card: CardEnum) => {
        if(shouldShowSuggestion && isBeginnerMode) {
            if(wantedResourcesSuggestion.includes(card)) onAddCard(card)
            return
        }
        onAddCard(card)
    }

    const cardToDisplayState = useMemo(() => {
        const activeCards = CardDataController.getCardEnumsOfTypes(gameModeData.activeResourceCardTypes)
        if(!isBankTradeMode && !isBeginnerMode) activeCards.push(CardEnum.AnyResource)
        return cardEnumsToCardState(activeCards)
    }, [gameModeData, isBankTradeMode, isBeginnerMode])

    const cardsToDisable = useMemo(() => cardEnumsToCardState(beginnerActionState?.resourceCardsPreventedFromTrading ?? []),
        [beginnerActionState?.resourceCardsPreventedFromTrading])
    const cardsToHighlight = useCardsToHighlight(TradeErrorHighlightLocationEnum.WantedSelector)

    return (
        <div className={clsx(styles.wantedCardSelectorContainer, className, {
            [styles.portraitLayout]: isPortraitLayout,
        })}>
            <div className={styles.cardsContainer}>
                <UIGameCardInventory
                    showTipTitle={false}
                    showTipBody={false}
                    forceShowCardBadges={false}
                    cardState={cardToDisplayState}
                    enablePartition={false}
                    onClickCard={handleAddCard}
                    className={styles.cardStack}
                    cardSize={isPortraitLayout ? 'xs' : 'base'}
                    cardsToHighlight={cardsToHighlight}
                    cardsToSuggestion={shouldShowSuggestion ? wantedResourcesSuggestion : []}
                    cardsToDisable={cardsToDisable}
                />
            </div>
            <UIGameTradeCreatorBankRatio isPortraitLayout={isPortraitLayout}/>
        </div>
    )
}
